import {
  Badge,
  Box,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";

const SOURCE_BADGE = {
  parsed: { label: "auto-filled", colorScheme: "purple" },
  default: { label: "default", colorScheme: "gray" },
  missing: { label: "needs your input", colorScheme: "red" },
};

export default function DynamicField({ field, value, onChange }) {
  const {
    key,
    label,
    type,
    options = [],
    placeholder,
    help_text,
    required,
    source,
    can_skip,
    skip_fallback,
  } = field;
  const badge = SOURCE_BADGE[source];
  // A "skipped" field carries an empty string (distinct from undefined,
  // which means "user hasn't touched it yet"). The compiler swaps in
  // skip_fallback for empty values when can_skip is set.
  const skipped = can_skip && value === "";

  const renderInput = () => {
    switch (type) {
      case "textarea":
        return (
          <Textarea
            value={value ?? ""}
            placeholder={placeholder || ""}
            onChange={(e) => onChange(key, e.target.value)}
            rows={3}
          />
        );
      case "number":
        return (
          <NumberInput value={value ?? ""} onChange={(_, n) => onChange(key, Number.isNaN(n) ? null : n)}>
            <NumberInputField placeholder={placeholder || ""} />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        );
      case "select":
        return (
          <Select
            value={value ?? ""}
            placeholder={placeholder || "Select an option"}
            onChange={(e) => onChange(key, e.target.value)}
          >
            {options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </Select>
        );
      case "multiselect":
        return (
          <CheckboxGroup
            value={Array.isArray(value) ? value : []}
            onChange={(vals) => onChange(key, vals)}
          >
            <Stack direction="row" wrap="wrap">
              {options.map((opt) => (
                <Checkbox key={opt} value={opt}>
                  {opt}
                </Checkbox>
              ))}
            </Stack>
          </CheckboxGroup>
        );
      case "phone":
      case "url":
      case "text":
      default:
        return (
          <Input
            type={type === "phone" ? "tel" : type === "url" ? "url" : "text"}
            value={value ?? ""}
            placeholder={placeholder || ""}
            onChange={(e) => onChange(key, e.target.value)}
          />
        );
    }
  };

  return (
    <FormControl isRequired={required && !skipped}>
      <HStack mb={1} spacing={2} justify="space-between">
        <HStack spacing={2}>
          <FormLabel mb={0}>{label}</FormLabel>
          {badge && (
            <Badge colorScheme={badge.colorScheme} variant="subtle" fontSize="0.65rem">
              {source === "parsed" ? "✨ " : ""}
              {badge.label}
            </Badge>
          )}
        </HStack>
        {can_skip && (
          <Checkbox
            size="sm"
            isChecked={skipped}
            onChange={(e) => onChange(key, e.target.checked ? "" : null)}
          >
            <Text fontSize="xs" color="gray.500">
              Skip
            </Text>
          </Checkbox>
        )}
      </HStack>
      <Box opacity={skipped ? 0.4 : 1} pointerEvents={skipped ? "none" : "auto"}>
        {renderInput()}
      </Box>
      {skipped && skip_fallback && (
        <FormHelperText color="purple.600">
          We'll use <strong>"{skip_fallback}"</strong> in your agent's prompt.
        </FormHelperText>
      )}
      {!skipped && help_text && <FormHelperText>{help_text}</FormHelperText>}
    </FormControl>
  );
}
